import { useEffect, useRef, useState } from 'react';
import { useReward } from '@/contexts/reward-context';
// import { cookielessFetcher } from '@api/fetcher';
import { addCommas } from '@/utils';
import { AwardType, RewardScene, VoucherInfo, VoucherType, WeeklyAwardResult } from '@/types';
import { oddsBoosterService } from '@/features/voucher/services/odds-booster.service';
import { siteService } from '@/services/site.service';
import { CloseIcon } from '@/components/svg-icons';
import { EventVoucherIcon } from '@/components/icons/event-voucher-icon';
import { RewardModalWrapper } from '@/components/reward-modal-wrapper';
import { Event3dButton } from '@/components/buttons/event-3d-button';

const getAwardAmount = (
	awards: WeeklyAwardResult['Awards'],
	type: number
): number => {
	return awards.find(award => award.AwardType === type)?.Amount ?? 0;
};

const RewardNotification = () => {

	const { labels } = useReward();

	return (
		<div className="reward-receive__notice reward-receive__frame">
			<div className="reward-receive__notice-title">
				{labels?.lbl_newclaimreward01}
			</div>
			<div
				className="reward-receive__notice-content"
				dangerouslySetInnerHTML={{ __html: labels?.lbl_newclaimreward02 ?? '' }}
			>
			</div>
		</div>
	)
}

export const RewardGift = () => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const {
		labels,
		planInfo,
		hasReward,
		promotionOddsBoostInfo,
		siteId,
		notUsedVoucherData,
		onGoToLeague,
		getAvailableVouchers,
		setRewardScene,
		setIsOpenedOutsideShop,
		fetchRewardInfoThenUpdate,
		receiveWeeklyAward,
		onClose,
		onKafkaSet
	} = useReward();
	const [isReceived, setIsReceived] = useState(false);
	const [weeklyAward, setWeeklyAward] = useState(planInfo?.WeeklyAward);
	const [vouchers, setVouchers] = useState<Array<VoucherInfo>>([]);
	const ranking = weeklyAward?.Ranking;
	const awards = weeklyAward?.Awards ?? [];

	const sabaCoin = getAwardAmount(awards, AwardType.SabaCoin);
	const riskFreeBetAmount = getAwardAmount(awards, AwardType.FreeRiskBet);
	const oddsBoosterAmount = getAwardAmount(awards, AwardType.OddsBooster);
	const newRiskFreeBetAmount = getAwardAmount(awards, AwardType.NewRiskFreeBet);
	const hasOddsBoosterPromotion = oddsBoosterService.hasOddsBoosterPromotion(promotionOddsBoostInfo);
	const hasVouchers = riskFreeBetAmount > 0 ||
		oddsBoosterAmount > 0 ||
		newRiskFreeBetAmount > 0 ||
		hasOddsBoosterPromotion;

	useEffect(() => {
		getAvailableVouchers()
			.then(data => {
				setVouchers(data);
			});
	}, []);

	const onReceive = () => {
		if (buttonRef.current) {
			buttonRef.current.disabled = true;
		}

		setIsReceived(true);

		receiveWeeklyAward().then(() => {
			setIsReceived(true);
			fetchRewardInfoThenUpdate();
		});
	};
	
	const goToShop = () => {
		onKafkaSet({
			type: 9,
			action: 4
		});
		setRewardScene(RewardScene.Shop);
	}
	
	// free oddsbooster voucher promotion
	const useVoucher = async () => {
		onKafkaSet({
			type: 9,
			action: 3
		});
		if (!notUsedVoucherData) return;
		try {
			switch (notUsedVoucherData?.VoucherType) {
				case VoucherType.FreeOddsBooster:
					await onGoToLeague?.({ sport: 1, leagueId: [3] });
					break;
				case VoucherType.AffPromotionOddsBoost:
					await onGoToLeague?.({ sport: 1, leagueId: [3, 4, 5, 8, 43] });
					break;
			}
			onClose();
		} catch (error) {
			console.error(error);
		}
	};

	const handleClose = () => {
		onKafkaSet({
			type: 9,
			action: 5
		});
		onClose();
	};

	const isSm88 = siteService.isSm88(siteId);

	return (
		<RewardModalWrapper>
			<div className="reward-receive" data-receive={isReceived}>
				{
					isSm88 && <div className="reward-receive__debet-logo"></div>
				}
				<div
					className="reward-receive__close"
					onClick={handleClose}
				>
					<CloseIcon />
				</div>
				<div className="reward-receive__bg" />
				<div className="reward-planet">
					<div className="reward-planet__header">
						<div className="reward-planet__title">{labels?.lbl_RPEventName}</div>
						<div className="reward-planet__subtitle">{labels?.lbl_RPClaimWeekR}</div>
					</div>
					<div className="reward-planet__main">
						<div className="reward-planet__lid" />
						<div className="reward-planet__collect-shine" />
						<div className="reward-planet__collect-base" />
						<div className="reward-planet__collect-more" />
						<div className="reward-planet__bowl" />
						<div className="reward-planet__coin1" />
						<div className="reward-planet__coin2" />
						<div className="reward-planet__coin3" />
						<div className="reward-planet__coin4" />

						{sabaCoin > 0 ? (
							<div className="reward-planet__msg">
								<div className="reward-planet__msg-title">{labels?.lbl_SabaLYouGot}</div>
								<div className="reward-planet__msg-earn">
									<i className="reward-iconcolor-shop-coin" />
									{addCommas(Math.round(sabaCoin * 100) / 100)}
								</div>
							</div>
						) : null}
					</div>
					<div className="reward-planet__fore">
						<div className="reward-planet__trans-light" />
					</div>
					<div className="reward-planet__back">
						<div className="reward-planet__star-l" />
						<div className="reward-planet__star-r" />
					</div>
				</div>
				<div className="reward-receive__main">
					<div className="reward-receive__frame reward-receive__frame--1">
						<div className="reward-receive__btn">
							<Event3dButton
								ref={buttonRef}
								onClick={onReceive}
							>
								{ isSm88 ? labels?.lbl_DebetObp03 : labels?.lbl_obp03}
							</Event3dButton>
						</div>
					</div>
					<div className="reward-receive__frame reward-receive__frame--2">
						<div className="reward-receive__vouchers">
							{oddsBoosterAmount > 0 ? (
								<div className="receive-voucher">
									<div className="receive-voucher__img">
										<div className="receive-voucher__img-light" />
										<EventVoucherIcon voucherType={VoucherType.OddsBooster} />
									</div>
									<span className="receive-voucher__amount">x{oddsBoosterAmount}</span>
								</div>
							) : null}

							{riskFreeBetAmount > 0 ? (
								<div className="receive-voucher">
									<div className="receive-voucher__img">
										<div className="receive-voucher__img-light" />
										<EventVoucherIcon voucherType={VoucherType.RiskFreeBet} />
									</div>
									<span className="receive-voucher__amount">x{riskFreeBetAmount}</span>
								</div>
							) : null}

							{newRiskFreeBetAmount > 0 ? (
								<div className="receive-voucher">
									<div className="receive-voucher__img">
										<div className="receive-voucher__img-light" />
										<EventVoucherIcon voucherType={VoucherType.NewRiskFreeBet} />
									</div>
									<span className="receive-voucher__amount">x{newRiskFreeBetAmount}</span>
								</div>
							) : null}
						</div>

						{hasOddsBoosterPromotion ? (
							<div className="reward-receive__gift">
								<div className="reward-receive__gift-logo"></div>
								<div className="reward-receive__gift-title">{labels?.lbl_obp10}</div>
								<div className="reward-receive__gift-voucher">
									<EventVoucherIcon
										voucherType={VoucherType.FreeOddsBooster}
										isGift={true}
									/>
									X{promotionOddsBoostInfo?.GiftOddsBoostTicketCount}
								</div>
							</div>
						) : null}

						{ranking ? (
							<div className="reward-receive__result">
								<div className="reward-receive__divider" />
								<div className="reward-receive__rank">
									<div className="reward-receive__rank-title">{labels?.lbl_RPRank}</div>
									<div className="reward-receive__rank-info">Top {ranking}</div>
								</div>
							</div>
						) : null}

						<div className="reward-receive__btn">
							<Event3dButton
								className="reward-btn-3d--normal"
								onClick={useVoucher}
							>
								{labels?.lbl_RPUtilizedVoucher}
							</Event3dButton>

							{hasReward ? (
								<Event3dButton onClick={goToShop}>
									{labels?.lbl_RPGoSabaShop}
								</Event3dButton>
							) : null}
						</div>
					</div>
				</div>

				{/*{<RewardNotification />}*/}
			</div>
		</RewardModalWrapper>
	);
};